import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { organizationsService } from '@/services/apiClient';
import type { UpdateOrganizationData } from '@/types/credits';

import { superAdminClientKeys } from './useSuperAdminClients';

/**
 * Organizations Hooks
 * React Query hooks for /api/v1/organizations/* endpoints
 */

// Query keys
export const organizationKeys = {
  all: ['organizations'] as const,
  lists: () => [...organizationKeys.all, 'list'] as const,
  list: (page: number, limit: number, orderDirection: 'ASC' | 'DESC') =>
    [...organizationKeys.lists(), { page, limit, orderDirection }] as const,
  detail: (id: string) => [...organizationKeys.all, 'detail', id] as const,
};

/**
 * Hook to fetch organizations with pagination
 */
export const useOrganizations = (
  page: number = 1,
  limit: number = 10,
  orderDirection: 'ASC' | 'DESC' = 'ASC'
) => {
  return useQuery({
    queryKey: organizationKeys.list(page, limit, orderDirection),
    queryFn: () => organizationsService.getOrganizations(page, limit, orderDirection),
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to fetch a single organization
 */
export const useOrganization = (organizationId: string) => {
  return useQuery({
    queryKey: organizationKeys.detail(organizationId),
    queryFn: () => organizationsService.getOrganization(organizationId),
    enabled: !!organizationId,
    staleTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to update an organization's editable fields (name, description, status).
 * Invalidates the org detail plus the super-admin client list/detail/analytics
 * so the client page and dashboard reflect the change.
 */
export const useUpdateOrganization = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateOrganizationData }) =>
      organizationsService.updateOrganization(id, data),
    onSuccess: (_result, { id }) => {
      void queryClient.invalidateQueries({ queryKey: organizationKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: superAdminClientKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: superAdminClientKeys.detail(id) });
      void queryClient.invalidateQueries({ queryKey: superAdminClientKeys.analytics() });
    },
  });
};
